import { useEffect, useMemo, useRef, useState, type ComponentProps } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { HomepageRenderer } from "@/components/admin/visual-editor/page-renderers/HomepageRenderer";
import { AboutPageRenderer } from "@/components/admin/visual-editor/page-renderers/AboutPageRenderer";
import { ServicesPageRenderer } from "@/components/admin/visual-editor/page-renderers/ServicesPageRenderer";
import { CustomPageRenderer } from "@/components/admin/visual-editor/page-renderers/CustomPageRenderer";
import { BuiltInPageExtras } from "@/components/admin/visual-editor/page-renderers/BuiltInPageExtras";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";

type EditorMessage =
  | { type: "EDITOR_READY" }
  | { type: "EDITOR_INIT"; payload: { page: string; content: unknown } }
  | { type: "EDITOR_SYNC"; payload: { page: string; content: unknown } }
  | { type: "EDITOR_UPDATE"; payload: { page: string; content: unknown } };

export default function EditorFrame() {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page") || "home";
  const pageId = searchParams.get("pageId") || null;

  const [content, setContent] = useState<unknown>(null);
  const [customPages, setCustomPages] = useState<{ id: string; slug: string }[]>([]);
  
  // Extra grace period: avoid flashing "not authenticated" during hydration
  const [gracePeriodComplete, setGracePeriodComplete] = useState(false);
  
  useEffect(() => {
    // Give auth a moment to hydrate; after 1.5s show the real auth state
    const timer = setTimeout(() => setGracePeriodComplete(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const parentWindow = useMemo(() => window.parent, []);
  const lastSentRef = useRef<string>("");

  // Fetch custom pages so the iframe can render them too
  useEffect(() => {
    const fetchCustomPages = async () => {
      const { data } = await supabase.from("custom_pages").select("id,slug");
      if (data) setCustomPages(data);
    };
    fetchCustomPages();
  }, []);

  // Tell parent we are ready to receive INIT
  useEffect(() => {
    const msg: EditorMessage = { type: "EDITOR_READY" };
    parentWindow?.postMessage(msg, window.location.origin);
  }, [parentWindow]);

  // Listen for INIT/SYNC from parent
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as EditorMessage | undefined;
      if (!data || typeof data !== "object") return;

      if (data.type === "EDITOR_INIT" || data.type === "EDITOR_SYNC") {
        if (data.payload?.page !== page) return;
        setContent(data.payload.content ?? null);
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [page]);

  // When local content changes (via inline editing), notify parent
  const handleChange = (next: unknown) => {
    setContent(next);

    try {
      const serialized = JSON.stringify(next);
      if (serialized === lastSentRef.current) return;
      lastSentRef.current = serialized;
    } catch {
      // ignore stringify failures
    }

    const msg: EditorMessage = {
      type: "EDITOR_UPDATE",
      payload: { page, content: next },
    };
    parentWindow?.postMessage(msg, window.location.origin);
  };

  // Show loader while auth is loading OR during initial grace period (whichever finishes first when auth is ready)
  const showLoader = loading || (!user && !gracePeriodComplete);

  if (showLoader) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  // After grace period, if still no user, show auth message (but NEVER redirect/navigate from iframe)
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full rounded-xl border border-border bg-card p-6">
          <h2 className="text-lg font-display text-foreground">Preview not authenticated</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            The editor preview runs in an iframe and needs an active login session. Please sign in again,
            then return to the Page Builder.
          </p>
          <a className="mt-4 inline-block text-sm text-primary underline" href="/auth" target="_top" rel="noreferrer">
            Go to sign in
          </a>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary title="Preview crashed while rendering">
      <div className="min-h-screen bg-background flex flex-col">
        {/* Navbar - disable interactions in editor mode */}
        <div className="pointer-events-none">
          <Navbar />
        </div>

        {/* Page Content */}
        <main className="flex-1">
          {page === "home" && <HomepageRenderer content={content as ComponentProps<typeof HomepageRenderer>["content"]} onChange={handleChange} />}
          {page === "about" && <AboutPageRenderer content={content as ComponentProps<typeof AboutPageRenderer>["content"]} onChange={handleChange} />}
          {page === "services" && <ServicesPageRenderer content={content as ComponentProps<typeof ServicesPageRenderer>["content"]} onChange={handleChange} />}
          {(page === "home" || page === "about" || page === "services") && <BuiltInPageExtras pageSlug={page} />}
          {page !== "home" && page !== "about" && page !== "services" && (
            <CustomPageRenderer
              sections={(content as ComponentProps<typeof CustomPageRenderer>["sections"]) || []}
              onChange={handleChange}
              pageId={pageId || customPages.find((p) => p.slug === page)?.id}
            />
          )}
        </main>

        {/* Footer - disable interactions in editor mode */}
        <div className="pointer-events-none">
          <Footer />
        </div>
      </div>
    </ErrorBoundary>
  );
}

